import { alpha, Box, Popover, styled, Tooltip, Typography } from '@mui/material'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MUIThemeColors } from 'src/mui-theme'
import { geometryColorPalette } from 'src/screens/localities/components/map/constraints'
import { getThemeColor } from 'src/utils'

export interface GeometryColorPickerProps {
  value?: string
  onChange: (color: string) => void
  label?: string
  disabled?: boolean
  size?: number
}

export const GeometryColorPicker: React.FC<GeometryColorPickerProps> = ({
  value,
  onChange,
  label,
  disabled = false,
  size = 24,
}) => {
  const { t } = useTranslation(['mooring-layout', 'common'])
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [draft, setDraft] = useState<string | null>(null)
  const selected = value ?? geometryColorPalette[0]
  const shown = draft ?? selected

  const pick = (color: string) => {
    setDraft(null)
    onChange(color)
    setAnchorEl(null)
  }

  const commitDraft = () => {
    if (draft && draft.toLowerCase() !== selected.toLowerCase()) onChange(draft)
    setDraft(null)
  }

  const close = () => {
    commitDraft()
    setAnchorEl(null)
  }

  return (
    <>
      <Tooltip title={label ?? (t('geometry-colour') as string)}>
        <StyledSwatchButton
          type="button"
          swatchColor={shown}
          swatchSize={size}
          isDisabled={disabled}
          aria-label={label ?? (t('geometry-colour') as string)}
          onClick={event => !disabled && setAnchorEl(event.currentTarget)}
        />
      </Tooltip>
      <StyledPopover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        marginThreshold={8}
      >
        <StyledPanel>
          <StyledSectionLabel variant="caption">{t('geometry-colour')}</StyledSectionLabel>
          <StyledPaletteGrid>
            {geometryColorPalette.map(color => (
              <StyledSwatchButton
                key={color}
                type="button"
                swatchColor={color}
                swatchSize={size}
                isDisabled={false}
                isSelected={color.toLowerCase() === shown.toLowerCase()}
                aria-label={color}
                onClick={() => pick(color)}
              />
            ))}
          </StyledPaletteGrid>
          <StyledDivider />
          <StyledCustomRow>
            <StyledSectionLabel variant="caption">{t('custom-colour')}</StyledSectionLabel>
            <StyledCustomField>
              <StyledColorInput
                type="color"
                value={shown}
                aria-label={t('custom-colour') as string}
                onChange={event => setDraft(event.target.value)}
                onBlur={commitDraft}
              />
              <StyledHexValue variant="caption">{shown}</StyledHexValue>
            </StyledCustomField>
          </StyledCustomRow>
        </StyledPanel>
      </StyledPopover>
    </>
  )
}

interface StyledSwatchProps {
  swatchColor: string
  swatchSize: number
  isDisabled: boolean
  isSelected?: boolean
}

const StyledPopover = styled(Popover)(({ theme }) => ({
  '& .MuiPopover-paper': {
    marginTop: theme.spacing(0.75),
    borderRadius: 12,
    border: `1px solid ${alpha(getThemeColor(theme, MUIThemeColors.black), 0.08)}`,
    boxShadow: `0 8px 28px ${alpha(getThemeColor(theme, MUIThemeColors.black), 0.16)}`,
  },
}))

const StyledPanel = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.75),
  width: 216,
  backgroundColor: getThemeColor(theme, MUIThemeColors.white),
}))

const StyledSectionLabel = styled(Typography)(({ theme }) => ({
  display: 'block',
  color: alpha(getThemeColor(theme, MUIThemeColors.black), 0.6),
}))

const StyledSwatchButton = styled('button', {
  shouldForwardProp: prop => !['swatchColor', 'swatchSize', 'isDisabled', 'isSelected'].includes(prop as string),
})<StyledSwatchProps>(({ theme, swatchColor, swatchSize, isDisabled, isSelected }) => ({
  width: swatchSize,
  height: swatchSize,
  padding: 0,
  flex: 'none',
  border: 'none',
  borderRadius: 2,
  cursor: isDisabled ? 'default' : 'pointer',
  opacity: isDisabled ? 0.5 : 1,
  backgroundColor: swatchColor,
  transition: theme.transitions.create(['transform', 'box-shadow'], { duration: 150 }),
  boxShadow: isSelected
    ? `0 0 0 2px ${getThemeColor(theme, MUIThemeColors.white)}, 0 0 0 4px ${getThemeColor(
        theme,
        MUIThemeColors.primaryMain,
      )}`
    : 'none',
  '&:hover': {
    transform: isDisabled ? 'none' : 'scale(1.12)',
  },
  '&:focus-visible': {
    outline: 'none',
    boxShadow: `0 0 0 2px ${getThemeColor(theme, MUIThemeColors.white)}, 0 0 0 4px ${getThemeColor(
      theme,
      MUIThemeColors.primaryMain,
    )}`,
  },
}))

const StyledPaletteGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(6, 1fr)',
  justifyItems: 'center',
  gap: theme.spacing(1),
  marginTop: theme.spacing(1.25),
}))

const StyledDivider = styled(Box)(({ theme }) => ({
  height: 1,
  margin: theme.spacing(1.75, -1.75, 1.5),
  backgroundColor: alpha(getThemeColor(theme, MUIThemeColors.black), 0.08),
}))

const StyledCustomRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(1),
}))

const StyledCustomField = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(0.5, 1, 0.5, 0.5),
  borderRadius: 8,
  border: `1px solid ${alpha(getThemeColor(theme, MUIThemeColors.black), 0.12)}`,
  transition: theme.transitions.create(['border-color', 'background-color'], { duration: 150 }),
  '&:hover': {
    borderColor: getThemeColor(theme, MUIThemeColors.primaryMain),
    backgroundColor: alpha(getThemeColor(theme, MUIThemeColors.primaryMain), 0.04),
  },
}))

const StyledHexValue = styled(Typography)(({ theme }) => ({
  color: alpha(getThemeColor(theme, MUIThemeColors.black), 0.7),
}))

const StyledColorInput = styled('input')({
  width: 26,
  height: 26,
  padding: 0,
  flex: 'none',
  cursor: 'pointer',
  background: 'none',
  border: 'none',
  borderRadius: 2,
  '&::-webkit-color-swatch-wrapper': {
    padding: 0,
  },
  '&::-webkit-color-swatch': {
    border: 'none',
    borderRadius: 2,
  },
  '&::-moz-color-swatch': {
    border: 'none',
    borderRadius: 2,
  },
})
